// Agent Portal (/cti2) — Audio settings: pick the mic + speaker the WebRTC
// softphone uses and test both. Opened from the Topbar account menu; works
// idle or mid-call (a mic change is swapped into the live call on save).
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useCallStore } from '@/router/zustand/callStore';
import { HAIRLINE } from '@/features/cti2/team/teamUi';
import notificationSound from '@/assets/sound/notificationSound.mp3';
import {
  applySpeaker,
  getSavedMicId,
  getSavedMicLabel,
  getSavedSpeakerId,
  getSavedSpeakerLabel,
  listDevices,
  micConstraint,
  pickDeviceId,
  saveAudioDevices,
  supportsSpeakerSelect,
  switchLiveMic,
  withSavedDevice,
} from '../audio/audioDevices';

const SELECT_CLASS = `w-full rounded-md border bg-background px-2 py-1.5 text-sm text-foreground ${HAIRLINE}`;

const readLoginType = (): string | null => {
  try { return sessionStorage.getItem('activeLoginType'); } catch { return null; }
};

export default function AudioSettingsModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [mics, setMics] = useState<MediaDeviceInfo[]>([]);
  const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>([]);
  const [micId, setMicId] = useState('');
  const [speakerId, setSpeakerId] = useState('');
  const [granted, setGranted] = useState(false);
  const [micError, setMicError] = useState('');
  const [saving, setSaving] = useState(false);
  const levelBarRef = useRef<HTMLDivElement | null>(null);
  const testAudioRef = useRef<HTMLAudioElement | null>(null);
  const canPickSpeaker = supportsSpeakerSelect();
  const loginType = open ? readLoginType() : null;

  // Draft starts from the saved choice on every open; Cancel just drops it.
  useEffect(() => {
    if (!open) return;
    setMicId(getSavedMicId());
    setSpeakerId(getSavedSpeakerId());
    setMicError('');
  }, [open]);

  // Device lists. Labels/ids stay blank until mic permission is granted, so
  // re-enumerate once the level meter's getUserMedia succeeds; devicechange
  // covers headsets plugged/unplugged while the dialog is open.
  useEffect(() => {
    if (!open || !navigator.mediaDevices) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        if (cancelled) return;
        const inputs = listDevices(all, 'audioinput');
        const outputs = listDevices(all, 'audiooutput');
        setMics(inputs);
        setSpeakers(outputs);
        // Empty list = no permission yet, not "device gone" — keep the draft.
        if (inputs.length) setMicId((cur) => pickDeviceId(cur, inputs));
        if (outputs.length) setSpeakerId((cur) => pickDeviceId(cur, outputs));
      } catch { /* insecure context — lists stay empty */ }
    };
    void load();
    navigator.mediaDevices.addEventListener('devicechange', load);
    return () => {
      cancelled = true;
      navigator.mediaDevices.removeEventListener('devicechange', load);
    };
  }, [open, granted]);

  // Mic test: live level bar from the draft mic. Also acts as the
  // permission prompt. Bar width is set directly on the DOM node so the
  // dialog doesn't re-render 60×/s.
  useEffect(() => {
    if (!open || !navigator.mediaDevices) return undefined;
    let stream: MediaStream | null = null;
    let ctx: AudioContext | null = null;
    let raf = 0;
    let cancelled = false;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: micConstraint(micId) });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        setGranted(true);
        setMicError('');
        ctx = new AudioContext();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        ctx.createMediaStreamSource(stream).connect(analyser);
        const buf = new Uint8Array(analyser.fftSize);
        const tick = () => {
          analyser.getByteTimeDomainData(buf);
          let peak = 0;
          for (const v of buf) peak = Math.max(peak, Math.abs(v - 128));
          if (levelBarRef.current) levelBarRef.current.style.width = `${Math.min(100, (peak / 64) * 100)}%`;
          raf = requestAnimationFrame(tick);
        };
        tick();
      } catch (e) {
        if (cancelled) return;
        setMicError(
          (e as DOMException)?.name === 'NotAllowedError'
            ? 'Microphone access is blocked. Allow it from the padlock icon in the address bar, then reopen this window.'
            : 'Could not open this microphone. Pick another one or check it is plugged in.',
        );
      }
    })();
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
      void ctx?.close();
      testAudioRef.current?.pause();
    };
  }, [open, micId]);

  const playTestSound = async () => {
    testAudioRef.current?.pause();
    const audio = new Audio(notificationSound);
    testAudioRef.current = audio;
    await applySpeaker(audio, speakerId);
    audio.play().catch(() => toast.error('Could not play the test sound.'));
  };

  const handleSave = async () => {
    setSaving(true);
    const micChanged = micId !== getSavedMicId();
    saveAudioDevices({
      micId,
      speakerId,
      micLabel: mics.find((d) => d.deviceId === micId)?.label || getSavedMicLabel(),
      speakerLabel: speakers.find((d) => d.deviceId === speakerId)?.label || getSavedSpeakerLabel(),
    });
    try {
      // callStore is @ts-nocheck, so its state types as unknown here.
      const { session } = useCallStore.getState() as { session?: unknown };
      if (micChanged && session) await switchLiveMic(session, micId);
      toast.success('Audio settings saved');
      onOpenChange(false);
    } catch {
      toast.error('Saved, but the mic could not be switched on this call. It will be used from your next call.');
    } finally {
      setSaving(false);
    }
  };

  const speakerOptions = (
    <>
      <option value="">System default</option>
      {withSavedDevice(speakers, speakerId, getSavedSpeakerLabel() || 'Saved speaker').map((d, i) => (
        <option key={d.deviceId} value={d.deviceId}>{d.label || `Speaker ${i + 1}`}</option>
      ))}
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[1100] max-w-md" overlayClassName="z-[1100]">
        <DialogHeader>
          <DialogTitle>Audio settings</DialogTitle>
        </DialogHeader>

        {loginType && loginType !== 'WEBRTC' && (
          <div className={`rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground ${HAIRLINE}`}>
            These settings apply to WebRTC calls. You are logged in on {loginType}, which uses that device&apos;s own audio.
          </div>
        )}

        <div className="space-y-1.5">
          <label htmlFor="cti-audio-mic" className="text-sm font-medium text-foreground">Microphone</label>
          <select id="cti-audio-mic" value={micId} onChange={(e) => setMicId(e.target.value)} className={SELECT_CLASS}>
            <option value="">System default</option>
            {withSavedDevice(mics, micId, getSavedMicLabel() || 'Saved microphone').map((d, i) => (
              <option key={d.deviceId} value={d.deviceId}>{d.label || `Microphone ${i + 1}`}</option>
            ))}
          </select>
          <div className="h-2 overflow-hidden rounded-full bg-muted" aria-hidden>
            <div ref={levelBarRef} className="h-full w-0 bg-success transition-[width] duration-75" />
          </div>
          {micError ? (
            <p className="text-xs text-destructive">{micError}</p>
          ) : (
            <p className="text-xs text-muted-foreground">Say something — the bar should move.</p>
          )}
        </div>

        <div className="space-y-1.5">
          <label htmlFor="cti-audio-speaker" className="text-sm font-medium text-foreground">Speaker</label>
          {canPickSpeaker ? (
            <div className="flex gap-2">
              <select id="cti-audio-speaker" value={speakerId} onChange={(e) => setSpeakerId(e.target.value)} className={SELECT_CLASS}>
                {speakerOptions}
              </select>
              <Button type="button" variant="outline" size="sm" onClick={playTestSound}>Test</Button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                This browser always uses the system default speaker. Change it in your computer&apos;s sound settings.
              </p>
              <Button type="button" variant="outline" size="sm" onClick={playTestSound}>Test</Button>
            </div>
          )}
          <p className="text-xs text-muted-foreground">Calls, ringtone and alerts play here.</p>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" onClick={handleSave} disabled={saving}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
